import { notNull } from '@gap/utils/utils'
import Box from '@mui/material/Box'
import AccessDenied from 'features/access-denied/AccessDenied'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import useInitialMount from 'reusable/hooks/useInitialMount'
import useStore from 'reusable/mobx-store-utils'
import DocViewStore from './DocViewStore'
import Markdown from 'reusable/markdown/Markdown'
import { LoadingModal } from 'reusable/mui/modals/LoadingModal'
import DocViewBreadcrumb from '../reusable/DocViewBreadcrumb'
import { DocViewStoreProvider } from './DocViewStore'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import { themeConfig } from '@gap/shared/theme'
import FlexAlignRight from 'reusable/mui/utilities/FlexAlignRight'
import OnlyForOrgType from 'reusable/containers/OnlyForOrgType'
import DownloadPdfButton from 'reusable/printing/DownloadPdfButton'
import ShareDocument from 'reusable/share-document-dialog/ShareDocument'
import Typography from '@mui/material/Typography'
import QuestionAnswerOutlined from '@mui/icons-material/QuestionAnswerOutlined'
import { SplitButton } from 'reusable/mui/split-button'
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'

/**
 * For viewing a document from the documents page
 */
export default observer(function DocView() {
   const urlParams = useParams()
   const orgGuardStore = useOrgGuardStore()
   const docViewStore = useStore((sp) => new DocViewStore(sp), {
      docId: notNull(urlParams.docId),
      collated: true,
      orgGuardStore
   })
   useInitialMount(docViewStore.fetch)

   if (!docViewStore.userHasAccessToThisDocument) {
      return <AccessDenied ParentWrapper={null} type="content-access-denied" sx={{ mt: 6 }} />
   }

   if (docViewStore.documentDoesNotExists) {
      return <AccessDenied ParentWrapper={null} type="document-no-longer-available" sx={{ mt: 6 }} />
   }

   if (docViewStore.loader.isLoading) {
      return <LoadingModal visible title={docViewStore.loader.loaderText || 'Loading document...'} />
   }

   return <DocViewStoreProvider store={docViewStore}>
      <DocViewBreadcrumb clientOrg={notNull(docViewStore.client)} docName={docViewStore.breadcrumbDocName} />
      <Stack direction="row" justifyContent="space-around" sx={{ pb: 4 }}>
         <Box sx={{ width: themeConfig.breakpoints.values.md }}>
            <Stack sx={{ flexDirection: 'row', justifyContent: 'right', mb: 2 }}>
               <OnlyForOrgType orgType="client">
                  <DownloadPdfButton docTitle={docViewStore.fileName} documentUrl={docViewStore.downloadLink} docId={docViewStore.sp.docId} />
               </OnlyForOrgType>
               <OnlyForOrgType orgType="advisor">
                  <ShareDocument document={docViewStore.document!} clientOrgId={docViewStore.document!.clientOrgId} />
                  {docViewStore.canEdit &&
                     <SplitButton
                        size='small'
                        variant="outlined"
                        sx={{ ml: 2 }}
                        options={[
                           { title: docViewStore.editState.opened ? 'Save' : 'Edit', callback: docViewStore.toggleEdit, loading: docViewStore.saveLoader.isLoading, hasPriority: true },
                           { title: 'Revert changes', callback: docViewStore.revertChanges },
                           { title: 'Download this as pdf', callback: docViewStore.downloadAsPdf },
                           ...(docViewStore.editState.opened ? [{ title: 'Discard changes', callback: docViewStore.discardChanges }] : []),
                        ]}
                     />
                  }
               </OnlyForOrgType>
            </Stack>
            <Paper elevation={12} sx={{ mt: 1, p: 6, pt: 5 }}>
               <Markdown markdown={docViewStore.markdownDocument} draggable={true} id={urlParams.docId!} readOnly={!docViewStore.editState.opened} onChange={docViewStore.updateMarkdown} sx={{
                  border: '1px solid #d1d5db',
               }} type={{ type: 'workspace', workItemId: docViewStore.sp.docId }} />
            </Paper>
         </Box>
      </Stack>
   </DocViewStoreProvider>
})

/**
 * For viewing a document in the utility panel
 */
export const DocViewUtilityPanel = observer(function DocViewUtilityPanel(props: { docId: string }) {
   const orgGuardStore = useOrgGuardStore()

   const docViewStore = useStore((sp) => new DocViewStore(sp), {
      docId: props.docId,
      collated: true,
      orgGuardStore
   })

   useInitialMount(docViewStore.fetch)

   if (!docViewStore.userHasAccessToThisDocument) {
      return <Box sx={{ mt: 6 }}>
         <AccessDenied ParentWrapper={null} type="content-access-denied" />
      </Box>
   }

   if (docViewStore.loader.isLoading) {
      return 'Loading'
   }

   if (!docViewStore.hasAnswers) {
      return <NoAnswersPlaceHolder />
   }

   return <DocViewStoreProvider store={docViewStore}>
      <Stack direction="row" justifyContent="space-around" sx={{ mt: 1 }}>
         <Markdown markdown={docViewStore.markdownDocument} id={props.docId} readOnly={true} sx={{
            width: '100%',
            border: '1px solid #d1d5db',
         }} type={{ type: 'workspace', workItemId: docViewStore.sp.docId }} />
      </Stack>
   </DocViewStoreProvider>
})

/**
 * View for guest users viewing their responses after submitting
 */
export const DocViewViewMyResponses = observer(function DocViewViewMyResponses() {
   const urlParams = useParams()
   const orgGuardStore = useOrgGuardStore()

   const docViewStore = useStore((sp) => new DocViewStore(sp), {
      docId: notNull(urlParams.docId),
      collated: false,
      orgGuardStore
   })

   useInitialMount(docViewStore.fetch)

   if (!docViewStore.userHasAccessToThisDocument) {
      return <Box sx={{ mt: 6 }}>
         <AccessDenied ParentWrapper={null} type="content-access-denied" />
      </Box>
   }

   if (docViewStore.documentDoesNotExists) {
      return <AccessDenied ParentWrapper={null} type="document-no-longer-available" sx={{ mt: 6 }} />
   }

   if (docViewStore.loader.isLoading) {
      return 'Loading'
   }

   return <Stack direction="row" justifyContent="space-around" sx={{ pb: 4 }}>
      <Box sx={{ width: themeConfig.breakpoints.values.md }}>
         <Stack sx={{ flexDirection: 'row', justifyContent: 'right', mb: 2, mt: 2 }}>
            <FlexAlignRight>
               <DownloadPdfButton docTitle={docViewStore.fileName} documentUrl={docViewStore.downloadLink} docId={docViewStore.sp.docId} />
            </FlexAlignRight>
         </Stack>
         <Paper elevation={12} sx={{ mt: 1, p: 6, pt: 5 }}>
            <Markdown markdown={docViewStore.markdownDocument} id={urlParams.docId!} readOnly={true} sx={{
               border: '1px solid #d1d5db',
            }} type={{ type: 'workspace', workItemId: docViewStore.sp.docId }} />
         </Paper>
      </Box>
   </Stack>
})

/**
 * Placeholder for when a document has no answers
 */
const NoAnswersPlaceHolder = observer(function NoAnswersPlaceHolder() {
   return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', m: '0 auto', gap: 1 }}>
      <QuestionAnswerOutlined sx={{ color: themeConfig.primary[600] }} />
      <Typography variant="body1Bold" sx={{ color: themeConfig.primary[600] }}>Nothing to see yet</Typography>
      <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>Responses will appear here once they've been submitted.</Typography>
   </Box>
})
